import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { type ReadonlyBox } from '../box/box';
import { vec2extended } from '../gl-matrix-extended/vec2-extended';
import { mod } from '../numbers';

// * all references to clockwise and anti-clockwise in this file
// assume our standard "+x is right, +y is down" world space

/**
 * An orthogonal direction, +x, +y, -x or -y
 * In world space these correspond to rightward, downward, leftward and upward
 */
export type Direction = '+x' | '+y' | '-x' | '-y';

/**
 * Tests if a direction is positive
 * @param dir
 * @returns true if positive, false if negative
 */
export function dirIsPositive(dir: Direction): boolean {
  return dir[0] === '+';
}

/**
 * Tests if a direction aligns to the x-axis
 * @param dir
 * @returns true if x-axis, false if y-axis
 */
export function dirIsXAxis(dir: Direction): boolean {
  return dir[1] === 'x';
}

/**
 * Tests if two directions are parallel
 * @param dirA
 * @param dirB
 * @returns boolean
 */
export function dirIsParallel(dirA: Direction, dirB: Direction): boolean {
  return dirA[1] === dirB[1];
}

/**
 * Tests if two directions are perpendicular
 * @param dirA
 * @param dirB
 * @returns boolean
 */
export function dirIsPerpendicular(dirA: Direction, dirB: Direction): boolean {
  return dirA[1] !== dirB[1];
}

/**
 * Tests if two directions are opposite
 * @param dirA
 * @param dirB
 * @returns boolean
 */
export function dirIsOpposite(dirA: Direction, dirB: Direction): boolean {
  return dirIsParallel(dirA, dirB) && dirA[0] !== dirB[0];
}

const DIRECTION_LEFT_TURNS: Record<Direction, Direction> = {
  '+x': '-y',
  '+y': '+x',
  '-x': '+y',
  '-y': '-x',
};

const DIRECTION_RIGHT_TURNS: Record<Direction, Direction> = {
  '+x': '+y',
  '+y': '-x',
  '-x': '-y',
  '-y': '+x',
};

/**
 * Rotates a direction to the left / anti-clockwise
 * If passed a boolean as a 2nd arg, this will turn left if true, or right if false
 * @param dir
 * @param left
 * @returns Direction
 */
export function dirTurnLeft(dir: Direction, left = true): Direction {
  return left ? DIRECTION_LEFT_TURNS[dir] : DIRECTION_RIGHT_TURNS[dir];
}

/**
 * Rotates a direction to the right / clockwise
 * If passed a boolean as a 2nd arg, this will turn right if true, or left if false
 * @param dir
 * @param right
 * @returns Direction
 */
export function dirTurnRight(dir: Direction, right = true): Direction {
  return right ? DIRECTION_RIGHT_TURNS[dir] : DIRECTION_LEFT_TURNS[dir];
}

const DIRECTION_UNIT_VECTORS = {
  '-x': vec2.fromValues(-1, 0),
  '+x': vec2.fromValues(1, 0),
  '-y': vec2.fromValues(0, -1),
  '+y': vec2.fromValues(0, 1),
} satisfies Record<Direction, ReadonlyVec2>;

/**
 * Converts a direction into a unit vector
 * "+x" becomes (1,0)
 * "-x" becomes (-1,0)
 * "+y" becomes (0,1)
 * "-y" becomes (0,-1)
 * @param dir
 * @returns vec2
 */
export function dirToVec2(dir: Direction): vec2 {
  return DIRECTION_UNIT_VECTORS[dir];
}

const DIRECTIONS_TO_QUARTER_TURNS = {
  '+x': 0,
  '+y': 1,
  '-x': 2,
  '-y': 3,
};

/**
 * Returns the number of positive / clockwise quarter-turns away from "x+" this directon is
 * "+x" returns 0
 * "+y" returns 1
 * "-x" returns 2
 * "-y" returns 3
 * @param dir
 * @returns number
 */
export function dirToQuarterTurns(dir: Direction): number {
  return DIRECTIONS_TO_QUARTER_TURNS[dir];
}

/**
 * Converts a direction into an angle in radians
 * "+x" becomes 0
 * "+y" becomes Math.PI * 0.5
 * "-x" becomes Math.PI
 * "-y" becomes Math.PI * -0.5
 * @param dir
 * @returns number
 */
export function dirToAngle(dir: Direction): number {
  const turns = dirToQuarterTurns(dir);
  return turns === 3 ? Math.PI * -0.5 : turns * Math.PI * 0.5;
}

/**
 * Gets the opposite direction for a direction
 * e.g. +x turns into -x, -y turns into +y
 * @param dir
 * @returns Direction
 */
export function dirToOpposite(dir: Direction): Direction {
  return dirSetSign(dir, !dirIsPositive(dir));
}

/**
 * Converts a direction into an axis
 * @param dir - The direction to get the axis of
 * @returns The axis of the direction
 */
export function dirToAxis(dir: Direction): 'x' | 'y' {
  return dir[1] === 'x' ? 'x' : 'y';
}

/**
 * Sets the sign of a direction, leaving the axis unchanged
 * @param dir
 * @param positive - true = sign will be positive, false = sign will be negative
 * @returns Direction
 */
export function dirSetSign(dir: Direction, positive: boolean): Direction {
  return `${positive ? '+' : '-'}${dir[1]}` as Direction;
}

/**
 * Sets the axis of a direction, leaving the sign unchanged
 * @param dir
 * @param xAxis - true = axis will be x-axis, false = axis will be y-axis
 * @returns Direction
 */
export function dirSetAxis(dir: Direction, xAxis: boolean): Direction {
  return `${dir[0]}${xAxis ? 'x' : 'y'}` as Direction;
}

/**
 * Creates a direction
 * @param positive - true = sign will be positive, false = sign will be negative
 * @param xAxis - true = axis will be x-axis, false = axis will be y-axis
 * @returns Direction
 */
export function dirFromSignAndAxis(positive: boolean, xAxis: boolean): Direction {
  return `${positive ? '+' : '-'}${xAxis ? 'x' : 'y'}` as Direction;
}

// There are several contributors to cumulative error, but in reasonable board use they should never exceed 0.1:
// - Length of the vector -> Error starts below 1e-5 and increases sub-linearly with a coefficient of 1e-6
// - Global coordinate -> Error reaches 0.1 at approx. 2.5M units away from the origin
// - Screen DPI on board creation -> Unconfirmed, appears to multiply error by 2-4x
// See https://product-fabric.atlassian.net/browse/CNS-16255 for additional details.
const EPSILON_FOR_DIRECTION = 0.1;
const MAX_EPSILON_FOR_DIRECTION_COEFFICIENT = 0.0001; // dirFromAngle and at least one other caller use a ~unit-length vector, so an epsilon of 0.1 is less appropriate

/**
 * Finds the direction best approximating a vec2
 * @param vector - the vec2 to round to a direction
 * @returns Direction
 */
export function dirFromVec2(vector: ReadonlyVec2): Direction {
  const [x, y] = vector;

  // Temporary fix as part of CNS-16255 to ensure callers with ~unit-length vectors don't have epsilons that are a large percentage of their total size
  // Will likely be replaced with individual caller's abilities to specify whether they want an epsilon at all
  const epsilon = Math.min(
    EPSILON_FOR_DIRECTION,
    MAX_EPSILON_FOR_DIRECTION_COEFFICIENT * vec2.len(vector),
  );

  // Bias imperceptibly towards the X-axis for vectors in intercardinal directions where cumulative errors oscillate between |x| and |y| being greater
  const xAxis = Math.abs(y) < Math.abs(x) + epsilon;

  const positive = xAxis ? x > 0 : y > 0;
  return dirFromSignAndAxis(positive, xAxis);
}

/**
 * Checks if a vector is approximately aligned to a cardinal direction
 * @param vector - the vec2 to check
 * @param epsilonForHorizontal - the threshold for whether the vector is in a horizontal cardinal direction
 * (defaults to EPSILON_FOR_DIRECTION as a relative threshold)
 * @param epsilonForVertical - the threshold for whether the vector is in a vertical cardinal direction
 * (defaults to EPSILON_FOR_DIRECTION as a relative threshold)
 * @returns true if the vector is in a cardinal direction within the epsilon threshold
 */
export function isCardinalDirection(
  vector: ReadonlyVec2,
  epsilonForHorizontal?: number,
  epsilonForVertical?: number,
): boolean {
  const [x, y] = vector;
  const len = vec2.len(vector);

  if (len === 0) {
    return false;
  }

  const absX = Math.abs(x);
  const absY = Math.abs(y);
  const maxAbs = Math.max(absX, absY);
  const minAbs = Math.min(absX, absY);

  if (maxAbs === 0) {
    return false;
  }

  const isHorizontal = absX > absY;
  const epsilon = isHorizontal
    ? (epsilonForHorizontal ?? EPSILON_FOR_DIRECTION)
    : (epsilonForVertical ?? EPSILON_FOR_DIRECTION);

  return minAbs < epsilon * maxAbs || minAbs < epsilon;
}

/**
 * Finds the direction best approximating the angle between one vec2 point and another
 * @param vector1
 * @param vector2
 * @returns Direction
 */
export function dirBetweenVec2s(vector1: ReadonlyVec2, vector2: ReadonlyVec2): Direction {
  return dirFromVec2(vec2.sub(vec2.create(), vector2, vector1));
}

const QUARTER_TURNS_TO_DIRECTIONS: Direction[] = ['+x', '+y', '-x', '-y'];

/**
 * Gets the direction after starting at "+x" and performing an amount of positive / clockwise quarter turns
 * @param amount - the amount of quarter turns to perform
 * @returns Direction
 */
export function dirFromQuarterTurns(amount: number): Direction {
  return QUARTER_TURNS_TO_DIRECTIONS[mod(amount, 4)];
}

/**
 * Gets the closest direction for an angle
 * @param angle - the angle in radians to round to a direction
 * @returns Direction
 */
export function dirFromAngle(angle: number): Direction {
  return dirFromVec2(vec2extended.fromPolar(angle, 1));
}

/**
 * Given a point on a box edge, return the outward direction perpendicular to the edge. Handles inverted coordinates.
 * Returns undefined if the point is not on a box edge.
 * @param point - Given a point on a box edge, return the outward direction perpendicular to the edge.
 * @param box - The box to get the edge from
 */
export function outDirFromPointOnBoxEdge(
  point: ReadonlyVec2,
  box: ReadonlyBox,
): Direction | undefined {
  if (point[0] === box.left) {
    return box.right > box.left ? '-x' : '+x';
  }
  if (point[0] === box.right) {
    return box.right > box.left ? '+x' : '-x';
  }
  if (point[1] === box.top) {
    return box.top > box.bottom ? '+y' : '-y';
  }
  if (point[1] === box.bottom) {
    return box.top > box.bottom ? '-y' : '+y';
  }
  return undefined;
}
